import React, { Fragment, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { uniqBy } from "lodash";
import { DropdownList, Multiselect } from "react-widgets";
import { TextInput, DateInput } from "../../../../components/form-group";
import ErrorMessage from "../../../../components/text/ErrorMessage";
import CreatableSelect from "react-select/creatable";

const NewRequirementForm: React.FC<any> = (props) => {
  const {
    formData,
    onSubmit,
    cities,
    microMarketsData,
    preferredLocationsData,
    projectType,
    propertyType,
    floorPreferences,
    facilities,
    handleCityZones,
    saving,
    isReset,
    resetForm,
    setReset,
    setResetForm,
  } = props;

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    errors,
    watch,
    trigger,
    getValues,
  } = useForm({
    defaultValues: formData,
    // defaultValues: requirements,
  });
  const [selectedValue, setSelectedValue] = useState<
    { name: string; value: string }[]
  >([]);

  useEffect(() => {
    if (resetForm) {
      reset(formData);
      setResetForm(false);
    }
  }, [resetForm]);

  let formValuesAfterReset = getValues();
  const hasValues = Object.values(formValuesAfterReset)?.some((value) => {
    return (
      value !== undefined &&
      value !== null &&
      value !== "" &&
      (!Array.isArray(value) || value.length !== 0)
    );
  });

  useEffect(() => {
    formValuesAfterReset = getValues();
  }, [
    watch("city"),
    watch("microMarkets"),
    watch("preferredLocations"),
    watch("propertyType"),
    watch("projectType"),
    watch("constructionStatus"),
    watch("minPrice"),
    watch("maxPrice"),
    watch("lastMoveInDate"),
    watch("earliestMoveInDate"),
    watch("maxPrice"),
    watch("minArea"),
    watch("maxArea"),
    watch("minSeats"),
    watch("maxSeats"),
    watch("floorPreferences"),
    watch("preferedProjects"),
    watch("preferredDevelopers"),
    watch("amenities"),
    watch("proposalDueBy"),
    watch("interiorType"),
    watch("proposalFormatType"),
    watch("proposalContentType"),
    watch("specialRequirements"),
    watch("forecast"),
    watch("certainity"),
    watch("constructionCompletionDate"),
    watch("weightage"),
    watch("includeGradeC"),
    watch("includeLeaseExpiry"),
  ]);

  useEffect(() => {
    if (hasValues && isReset) {
      setReset(false);
    }
    if (!hasValues && !isReset) {
      setReset(true);
    }
  }, [hasValues]);

  enum ConstructionStatus {
    Upcoming = "upcoming",
    ReadyToMove = "readyToMove",
    UnderConstruction = "underConstruction",
    Operational = "operational",
    ReadyForFitOuts = "readyForFitOuts",
  }

  enum InteriorType {
    BareShell = "Bare Shell",
    Warmshell = "Warm Shell",
    SemiFurnished = "Semi Furnished",
    PreFurnished = "Pre Furnished",
    FullyFurnished = "Fully Furnished",
  }


  const addSpacesToEnumValue = (value: string): string =>
    value?.replace(/([a-z])([A-Z])/g, "$1 $2");

  const minArea = watch("minArea");
  const maxArea = watch("maxArea");
  const minPrice = watch("minPrice");
  const maxPrice = watch("maxPrice");
  const minSeats = watch("minSeats");
  const maxSeats = watch("maxSeats");
  const selectedPropertyType =
    propertyType?.find(
      (prptyType: { value: any }) => prptyType.value === watch("propertyType")
    )?.name ||
    watch("propertyType")?.name ||
    "";
  const selectedProjectType =
    projectType?.find(
      (projType: { value: any }) => projType.value === watch("projectType")
    )?.name ||
    watch("projectType")?.name ||
    "";

  const selectedConstrutionStatus = watch("constructionStatus") || [];

  useEffect(() => {
    trigger("maxPrice");
    trigger("minPrice");
    trigger("lastMoveInDate");
    trigger("earliestMoveInDate");
    trigger("minSeats");
    trigger("maxSeats");
  }, [
    watch("minPrice"),
    watch("maxPrice"),
    watch("lastMoveInDate"),
    watch("earliestMoveInDate"),
    watch("maxPrice"),
    watch("minSeats"),
    watch("maxSeats"),
  ]);

  const DropdownIndicator = () => {
    return <div></div>;
  };

  useEffect(() => {
    const valuesToMatch = watch("microMarkets").map((obj: any) => obj.value);

    const matchedValues: any = [];
    microMarketsData.map((obj: any) => {
      if (valuesToMatch.includes(obj.value)) {
        matchedValues.push(...obj.cityLocations);
      }
    });

    const tempPreferredLocations = [
      ...matchedValues.map((item: { _id: string; cityLocName: string }) => ({
        value: item._id,
        name: item.cityLocName,
      })),
    ];

    setValue("preferredLocations", uniqBy(tempPreferredLocations, "value"));
  }, [watch("microMarkets")]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="col-12 row">
        <div className="col-4 mb-2 pb-1">
          <label className="font-sm font-weight-semibold mb-1" htmlFor="city">
            City:
          </label>
          <Controller
            name="city"
            control={control}
            rules={{
              required: {
                value: true,
                message: "City is required",
              },
            }}
            defaultValue={formData?.city}
            render={({ onChange, value }) => (
              <DropdownList
                value={cities?.length ? value : []}
                data={cities}
                busy={cities?.length === 0}
                textField="name"
                valueField={"value"}
                filter="contains"
                onChange={(val) => {
                  onChange(val);
                  handleCityZones(val?.value);
                  setValue("microMarkets", []);
                  setValue("preferredLocations", []);
                }}
                placeholder="Select City"
              />
            )}
          />
          {errors.city && <ErrorMessage>{errors.city.message}</ErrorMessage>}
        </div>

        <div className="col-4 mb-2 pb-1">
          <label
            className="font-sm font-weight-semibold mb-1"
            htmlFor="microMarkets"
          >
            Micro Markets:<span className="text-danger">*</span>
          </label>
          <Controller
            name="microMarkets"
            control={control}
            rules={{
              required:
                "Please select either Micro Markets or Preferred Locations",
              validate: (value) => {
                return (
                  !!(value.length > 0) ||
                  !!(watch("preferredLocations").length > 0)
                );
              },
            }}
            defaultValue={formData?.microMarkets}
            render={({ onChange, value }) => (
              <Multiselect
                value={microMarketsData?.length ? value : []}
                data={microMarketsData}
                textField={"name"}
                valueField={"value"}
                onChange={(val) => {
                  onChange(val);
                  trigger("microMarkets");
                }}
                placeholder="Search"
              />
            )}
          />
          {errors.microMarkets && (
            <ErrorMessage>
              Please select either Micro Markets or Preferred Locations
            </ErrorMessage>
          )}
        </div>
        <div className="col-4 mb-2 pb-1">
          <label
            className="font-sm font-weight-semibold mb-1"
            htmlFor="preferredLocations"
          >
            Preferred Locations:
          </label>
          <Controller
            name="preferredLocations"
            control={control}
            defaultValue={formData?.preferredLocations}
            render={({ onChange, value }) => (
              <Multiselect
                value={preferredLocationsData?.length ? value : []}
                data={preferredLocationsData}
                textField={"name"}
                valueField={"value"}
                onChange={(val) => {
                  onChange(val);
                  trigger("preferredLocations");
                }}
                placeholder="Search Locations"
              />
            )}
          />
          {errors.preferredLocations && (
            <ErrorMessage>Preferred Locations is required</ErrorMessage>
          )}
        </div>

        <div className="col-4 mb-2 pb-1">
          <label
            className="font-sm font-weight-semibold mb-1"
            htmlFor="propertyType"
          >
            Property Type:<span className="text-danger">*</span>
          </label>
          <Controller
            name="propertyType"
            control={control}
            rules={{
              required: {
                value: true,
                message: "Property Type is required",
              },
            }}
            defaultValue={formData?.propertyType}
            render={({ onChange, value }) => (
              <DropdownList
                value={value}
                data={propertyType}
                textField={"name"}
                valueField={"value"}
                onChange={(val) => {
                  onChange(val);
                  trigger("propertyType");
                }}
                placeholder="Select Property Type"
              />
            )}
          />
          {errors.propertyType && (
            <ErrorMessage>{errors.propertyType.message}</ErrorMessage>
          )}
        </div>

        <div className="col-4 mb-2 pb-1 ">
          <label
            className="font-sm font-weight-semibold mb-1"
            htmlFor="projectType"
          >
            Project Type:<span className="text-danger">*</span>
          </label>
          <Controller
            name="projectType"
            control={control}
            rules={{
              required: {
                value: true,
                message: "Project Type is required",
              },
              validate: (value) => {
                return !!(value.length > 0);
              },
            }}
            defaultValue={formData?.projectType}
            render={({ onChange, value }) => (
              <Multiselect
                value={projectType?.length ? value : []}
                data={projectType}
                textField={"name"}
                valueField={"value"}
                onChange={(val) => {
                  onChange(val);
                  trigger("projectType");
                  // onChange(val?.map(x => x.value));
                }}
                placeholder="Search Project Types"
              />
            )}
          />
          {errors.projectType && (
            <ErrorMessage>Project Type is required</ErrorMessage>
          )}
        </div>
      </div>
      <div>
        <div
          className="px-3 py-3  mb-3 py-1 font-normal font-weight-semibold bg-gray-100 mt-3 mb-4"
          style={{ margin: "0 15px" }}
        >
          Additional Property Information
        </div>
        <div className="row">
          {(selectedPropertyType === "Business Center & Co-working Space" ||
            selectedProjectType === "Business Center & Co-working Space") && (
              <div className="col-md-4">
                <Fragment>
                  <div className="col-12  font-normal font-weight-semibold mb-3">
                    Select Seats:
                  </div>
                  <div className="row mx-0">
                    <div className="col-6 mb-2 pb-1 textInput-component-style">
                      <label
                        className="font-sm font-weight-semibold mb-1"
                        htmlFor="minSeats"
                      >
                        Min Seats:
                        {selectedPropertyType ===
                          "Business Center & Co-working Space" ||
                          selectedProjectType ===
                          "Business Center & Co-working Space" ? (
                          <span className="text-danger">*</span>
                        ) : null}
                      </label>
                      <Controller
                        name="minSeats"
                        control={control}
                        rules={{
                          required: {
                            value:
                              selectedPropertyType ===
                                "Business Center & Co-working Space" ||
                                selectedProjectType ===
                                "Business Center & Co-working Space"
                                ? true
                                : false,
                            message: "Min Seats is required",
                          },
                          validate: (value) => {
                            const numericMaxSeats = maxSeats;
                            if (
                              numericMaxSeats &&
                              (value === undefined ||
                                value === null ||
                                value === "")
                            ) {
                              return "Min Seats is required";
                            }

                            return undefined;
                          },
                        }}
                        render={({ onChange, value, name }) => (
                          <TextInput
                            type="number"
                            placeholder="Min Seats"
                            onChange={onChange}
                            name={name}
                            default={value}
                            gap="no"
                            clearFeildValue={true}
                          />
                        )}
                      />
                      {errors.minSeats && (
                        <ErrorMessage>{errors.minSeats.message}</ErrorMessage>
                      )}
                    </div>
                    <div className="col-6 mb-2 pb-1 textInput-component-style">
                      <label
                        className="font-sm font-weight-semibold mb-1"
                        htmlFor="maxSeats"
                      >
                        Max Seats:
                        {selectedPropertyType ===
                          "Business Center & Co-working Space" ||
                          selectedProjectType ===
                          "Business Center & Co-working Space" ? (
                          <span className="text-danger">*</span>
                        ) : null}
                      </label>

                      <Controller
                        name="maxSeats"
                        control={control}
                        rules={{
                          required: {
                            value:
                              selectedPropertyType ===
                                "Business Center & Co-working Space" ||
                                selectedProjectType ===
                                "Business Center & Co-working Space"
                                ? true
                                : false,
                            message: "Max Seats is required",
                          },
                          validate: (value) => {
                            const numericValue = value;
                            const tempMinSeats = minSeats;
                            return (tempMinSeats || tempMinSeats === 0) &&
                              numericValue !== 0 &&
                              !numericValue
                              ? "Max Seats is required"
                              : (!numericValue && numericValue !== 0) ||
                              (!tempMinSeats && tempMinSeats !== 0) ||
                              (numericValue && numericValue > tempMinSeats) ||
                              (numericValue === 0 && tempMinSeats === 0) ||
                              "Max Seats should be greater than Min Seats";
                          },
                        }}
                        render={({ onChange, value, name }) => (
                          <TextInput
                            type="number"
                            placeholder="Max Seats"
                            onChange={onChange}
                            name={name}
                            default={value}
                            gap="no"
                            clearFeildValue={true}
                          />
                        )}
                      />
                      {errors.maxSeats && (
                        <ErrorMessage>{errors.maxSeats.message}</ErrorMessage>
                      )}
                    </div>
                  </div>
                </Fragment>
              </div>
            )}

          <div className="col-md-4">
            <div className="row mx-0">
              <div
                className={`col-12 font-normal font-weight-semibold mb-3 
          ${(selectedPropertyType === "Business Center & Co-working Space" ||
                    selectedProjectType === "Business Center & Co-working Space") &&
                  ""
                  }`}
              >
                Select Budget(Per Sq. Ft.)
              </div>
              <div className="col-6 mb-2 pb-1 textInput-component-style">
                <label
                  className="font-sm font-weight-semibold mb-1"
                  htmlFor="minPrice"
                >
                  Min Price:
                </label>
                <Controller
                  name="minPrice"
                  control={control}
                  rules={{
                    validate: (value) => {
                      const numericMaxPrice = maxPrice;
                      if (
                        numericMaxPrice &&
                        (value === undefined || value === null || value === "")
                      ) {
                        return "Min Price is required";
                      }

                      return undefined;
                    },
                  }}
                  defaultValue={formData?.minPrice}
                  render={({ onChange, value, name }) => (
                    <TextInput
                      type="number"
                      placeholder="Min Price"
                      onChange={onChange}
                      name={name}
                      default={value}
                      gap="no"
                      clearFeildValue={true}
                    />
                  )}
                />
                {errors.minPrice && (
                  <ErrorMessage>{errors.minPrice.message}</ErrorMessage>
                )}
              </div>
              <div className="col-6 mb-2 pb-1 textInput-component-style">
                <label
                  className="font-sm font-weight-semibold mb-1"
                  htmlFor="maxPrice"
                >
                  Max Price:
                </label>

                <Controller
                  name="maxPrice"
                  control={control}
                  rules={{
                    validate: (value) => {
                      const numericValue = value;
                      const tempMinPrice = minPrice;
                      return (tempMinPrice || tempMinPrice === 0) &&
                        numericValue !== 0 &&
                        !numericValue
                        ? "Max Price is required"
                        : (!numericValue && numericValue !== 0) ||
                        (!tempMinPrice && tempMinPrice !== 0) ||
                        (numericValue && numericValue > tempMinPrice) ||
                        (numericValue === 0 && tempMinPrice === 0) ||
                        "Max Price must be greater than Min Price";
                    },
                  }}
                  defaultValue={formData?.maxPrice}
                  render={({ onChange, value, name }) => (
                    <TextInput
                      type="number"
                      placeholder="Max Price"
                      onChange={onChange}
                      name={name}
                      default={value}
                      gap="no"
                      clearFeildValue={true}
                    />
                  )}
                />
                {errors.maxPrice && (
                  <ErrorMessage>{errors.maxPrice.message}</ErrorMessage>
                )}
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="row mx-0">
              <div className="col-12 font-normal font-weight-semibold mb-3">
                Area Offered(Sq. Ft.)
              </div>

              <div className="col-6 mb-2 pb-1 textInput-component-style">
                <label
                  className="font-sm font-weight-semibold mb-1"
                  htmlFor="minArea"
                >
                  Min Area:<span className="text-danger">*</span>
                </label>
                <Controller
                  name="minArea"
                  control={control}
                  rules={{
                    required: "Min Area is required",
                    validate: (value) => {
                      const numericMaxArea = maxArea;
                      if (
                        numericMaxArea &&
                        (value === undefined || value === null || value === "")
                      ) {
                        return "Min Area is required";
                      }
                      if (value < 1) {
                        return "Min Area must be greater than 0";
                      }

                      return undefined;
                    },
                  }}
                  defaultValue={formData?.minArea}
                  render={({ onChange, value, name }) => (
                    <TextInput
                      type="number"
                      placeholder="Min Area"
                      onChange={(dat) => {
                        onChange(dat);
                        trigger("minArea");
                      }}
                      name={name}
                      default={value}
                      gap="no"
                      clearFeildValue={true}
                    />
                  )}
                />
                {errors.minArea && (
                  <ErrorMessage>{errors.minArea.message}</ErrorMessage>
                )}
              </div>
              <div className="col-6 mb-2 pb-1 textInput-component-style">
                <label
                  className="font-sm font-weight-semibold mb-1"
                  htmlFor="maxArea"
                >
                  Max Area:<span className="text-danger">*</span>
                </label>
                <Controller
                  name="maxArea"
                  control={control}
                  rules={{
                    required: "Max Area is required",
                    validate: (value) => {
                      const numericValue = value;
                      const tempMinArea = minArea;
                      return (tempMinArea || tempMinArea === 0) &&
                        numericValue !== 0 &&
                        !numericValue
                        ? "Max Area is required"
                        : (!numericValue && numericValue !== 0) ||
                        (!tempMinArea && tempMinArea !== 0) ||
                        (numericValue && numericValue > tempMinArea) ||
                        (numericValue === 0 && tempMinArea === 0) ||
                        "Max Area must be greater than Min Area";
                    },
                  }}
                  defaultValue={formData?.maxArea}
                  render={({ onChange, value, name }) => (
                    <TextInput
                      type="number"
                      placeholder="Max Area"
                      onChange={(dat) => {
                        onChange(dat);
                        trigger("maxArea");
                      }}
                      name={name}
                      default={value}
                      gap="no"
                      clearFeildValue={true}
                    />
                  )}
                />
                {errors.maxArea && (
                  <ErrorMessage>{errors.maxArea.message}</ErrorMessage>
                )}
              </div>
            </div>
          </div>

          {(selectedPropertyType === "Business Center & Co-working Space" ||
            selectedProjectType === "Business Center & Co-working Space") && (
              <div className="col-4">
                <div className="row mx-0">
                  <Fragment>
                    <div className="col-12 font-normal font-weight-semibold pt-3 mb-2">
                      <label
                        className="font-sm font-weight-semibold mb-1"
                        htmlFor="facilityType"
                      >
                        Facility Type:
                        {selectedPropertyType ===
                          "Business Center & Co-working Space" ||
                          selectedProjectType ===
                          "Business Center & Co-working Space" ? (
                          <span className="text-danger">*</span>
                        ) : null}
                      </label>
                    </div>

                    <div className="col-12 mb-2 pb-1">
                      <Controller
                        name="facilityType"
                        control={control}
                        rules={{
                          required: {
                            value:
                              selectedPropertyType ===
                                "Business Center & Co-working Space" ||
                                selectedProjectType ===
                                "Business Center & Co-working Space"
                                ? true
                                : false,
                            message: "Facility Type is required",
                          },
                        }}
                        render={({ onChange, value }) => (
                          <DropdownList
                            value={value}
                            data={facilities}
                            textField={"name"}
                            valueField={"value"}
                            onChange={(selectedValues) => {
                              onChange(selectedValues);
                              trigger("facilityType");
                            }}
                            placeholder="Select Facility Type"
                          />
                        )}
                      />
                      {errors.facilityType && (
                        <ErrorMessage>{errors.facilityType.message}</ErrorMessage>
                      )}
                    </div>
                  </Fragment>
                </div>
              </div>
            )}

          <div className="col-4">
            {!(
              selectedPropertyType === "Business Center & Co-working Space" ||
              selectedProjectType === "Business Center & Co-working Space"
            ) && (
                <div className="col-12 font-normal font-weight-semibold">
                  &nbsp;
                </div>
              )}

            <div className="row mx-0">
              <div className="col-12 font-normal font-weight-semibold pt-3 mb-2 ">
                Move-in Date
              </div>
              <div className="col-6">
                <>
                  <Controller
                    control={control}
                    name="earliestMoveInDate"
                    render={({ onChange, value }) => (
                      <DateInput
                        default={value}
                        placeholder="Earliest Move in Date"
                        onChange={onChange}
                      />
                    )}
                  />
                  {errors.earliestMoveInDate && (
                    <ErrorMessage>
                      {errors.earliestMoveInDate.message}
                    </ErrorMessage>
                  )}
                </>
              </div>
              <div className="col-6">
                <>
                  <Controller
                    control={control}
                    rules={{
                      validate: {
                        dateComparison: (value) => {
                          const earliestMoveInDate = watch(
                            "earliestMoveInDate"
                          ); // Get the value of the "earliestMoveInDate" field
                          if (earliestMoveInDate && value) {
                            return (
                              new Date(earliestMoveInDate) < new Date(value) ||
                              "Last Move-in Date should be greater than Earliest Move-in Date"
                            );
                          }
                        },
                      },
                    }}
                    name="lastMoveInDate"
                    render={({ onChange, value }) => (
                      <DateInput
                        default={value}
                        placeholder="Last Move-in Date"
                        onChange={onChange}
                      />
                    )}
                  />
                  {errors.lastMoveInDate && (
                    <ErrorMessage>{errors.lastMoveInDate.message}</ErrorMessage>
                  )}
                </>
              </div>
            </div>
          </div>
          <div className="col-4">
            <div className="row mx-0">
              <div className="col-12 font-normal font-weight-semibold pt-3 mb-2 ">
                Floor Preferences
              </div>
              <div className="col-12 mb-2 pb-1">
                <Controller
                  name="floorPreferences"
                  control={control}
                  defaultValue={formData?.floorPreferences}
                  render={({ onChange, value }) => (
                    <Multiselect
                      value={floorPreferences?.length ? value : []}
                      data={floorPreferences}
                      textField={"name"}
                      valueField={"value"}
                      onChange={(val) => {
                        onChange(val);
                      }}
                      filter="startsWith"
                      messages={
                        floorPreferences?.length === 0
                          ? { emptyList: "No data found" }
                          : {}
                      }
                      placeholder="Select Floor"
                    />
                  )}
                />
                {errors.floorPreferences && (
                  <ErrorMessage>Floor Preferences is required</ErrorMessage>
                )}
              </div>
            </div>
          </div>

          <div className="col-4">
            <div className="row mx-0">
              <div className="col-12 mb-2 pb-1 pt-3">
                <label className="font-normal font-weight-semibold">
                  {" "}
                  Construction Status
                </label>
                <Controller
                  name="constructionStatus"
                  control={control}
                  render={({ onChange, value }) => (
                    <Multiselect
                      value={value}
                      data={Object.entries(ConstructionStatus).map(
                        ([name, val]) => ({
                          name: addSpacesToEnumValue(name),
                          value: val,
                        })
                      )}
                      textField="name"
                      valueField="value"
                      placeholder="Select"
                      onChange={(val) => {
                        onChange(val);
                        trigger("constructionStatus");
                        setSelectedValue(val);
                        const selectedValues = val?.map((x) => x.value) || [];
                        if (
                          !selectedValues?.includes(
                            ConstructionStatus.UnderConstruction
                          )
                        ) {
                          setValue("constructionCompletionDate", "");
                        }
                      }}
                    />
                  )}
                />
                {errors.constructionStatus && (
                  <ErrorMessage>Construction Status is required</ErrorMessage>
                )}
              </div>
            </div>
          </div>

          {selectedConstrutionStatus &&
            selectedConstrutionStatus.some(
              (item: any) =>
                item?.value === ConstructionStatus.UnderConstruction
            ) && (
              <div className="col-4">
                <div className="row mx-0">
                  <div className="col-12 mb-2 pb-1 pt-3">
                    <label className="font-normal font-weight-semibold">
                      Construction Completion Date
                    </label>
                    <Controller
                      control={control}
                      name={`constructionCompletionDate`}
                      render={({ onChange, value }) => (
                        <DateInput
                          default={value}
                          placeholder="Date"
                          onChange={(val) => {
                            onChange(val);
                            trigger("constructionCompletionDate");
                          }}
                          minDate={new Date()}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            )}

          <div className="col-4">
            <div className="row mx-0">
              <div className="col-12 font-normal font-weight-semibold pt-3 mb-2  pt-3">
                Interior Type
              </div>
              <div className="col-12 mb-2 pb-1">
                <Controller
                  name="interiorType"
                  control={control}
                  render={({ onChange, value }) => (
                    <Multiselect
                      value={value}
                      data={Object.entries(InteriorType).map(([name, val]) => ({
                        name: addSpacesToEnumValue(name),
                        value: val,
                      }))}
                      textField="name"
                      valueField="value"
                      placeholder="Select"
                      onChange={(val) => {
                        onChange(val);
                      }}
                    />
                  )}
                />
              </div>
            </div>
          </div>
          <div className="col-4">
            <div className="col-12">
              {(selectedPropertyType === "Business Center & Co-working Space" ||
                selectedProjectType ===
                "Business Center & Co-working Space") && (
                  <div>
                    <label>&nbsp;</label>
                  </div>
                )}

              <div className="mt-3">
                <span className="mr-4">
                  <Controller
                    control={control}
                    name="includeGradeC"
                    render={({ onChange, value, name }) => (
                      <Fragment>
                        <input
                          type="checkbox"
                          id={name}
                          checked={value}
                          onChange={(e) => onChange(e.target.checked)}
                        />
                        <label htmlFor={name}>Include Grade C</label>
                      </Fragment>
                    )}
                  />
                </span>
                <span className="d-inline-block">
                  <Controller
                    control={control}
                    name="includeLeaseExpiry"
                    render={({ onChange, value, name }) => (
                      <Fragment>
                        <input
                          type="checkbox"
                          id={name}
                          checked={value}
                          onChange={(e) => onChange(e.target.checked)}
                        />
                        <label htmlFor={name}>
                          Include Lease Expiry Options
                        </label>
                      </Fragment>
                    )}
                  />
                </span>
              </div>
              {!(
                selectedPropertyType === "Business Center & Co-working Space" ||
                selectedProjectType === "Business Center & Co-working Space"
              ) && <div className="mb-4"></div>}
            </div>
          </div>
        </div>
      </div>
      <div className="col-12 row">
        <div
          className="col-12 font-normal font-weight-semibold py-3  mt-3 bg-gray-100"
          style={{ margin: "0 15px" }}
        >
          Proposal Details
        </div>
        <div className="row mx-0 w-100 pt-3">
          <div className="col-3">
            <label className="font-sm font-weight-semibold mb-2">
              {" "}
              Proposal Due By
            </label>
            <div className="row">
              <div className="col-12 mb-2 pb-1">
                <>
                  <Controller
                    control={control}
                    name="proposalDueBy"
                    render={({ onChange, value }) => (
                      <DateInput
                        default={value}
                        placeholder="Choose Date"
                        onChange={onChange}
                        minDate={new Date()}
                      />
                    )}
                  />
                  {errors.proposaldueby && (
                    <ErrorMessage>{errors.proposaldueby.message}</ErrorMessage>
                  )}
                </>
              </div>
            </div>
          </div>
          <div className="col-5">
            <div className="row">
            </div>
          </div>
        </div>

        <div className="col-12" />

        <div
          className="col-12 font-normal font-weight-semibold py-3  mt-3 bg-gray-100 mb-3"
          style={{ margin: "0 15px" }}
        >
          Forecast & Certainity
        </div>
        <div className="col-2 mb-2 pb-1 textInput-component-style">
          <label
            className="font-sm font-weight-semibold mb-1"
            htmlFor="forecast"
          >
            Forecast (Amount):
          </label>
          <Controller
            name="forecast"
            control={control}
            render={({ onChange, value, name }) => (
              <TextInput
                type="number"
                placeholder="Forecast"
                onChange={onChange}
                name={name}
                default={value}
                gap="no"
                clearFeildValue={true}
                step="any"
                maxValue={"100000000"}
              />
            )}
          />
        </div>

        <div className="col-2 mb-2 pb-1 textInput-component-style">
          <label
            className="font-sm font-weight-semibold mb-1"
            htmlFor="certainity"
          >
            Certainity (%):
          </label>
          <Controller
            name="certainity"
            control={control}
            render={({ onChange, value, name }) => (
              <TextInput
                type="number"
                placeholder="Certainity"
                onChange={onChange}
                name={name}
                default={value}
                gap="no"
                clearFeildValue={true}
                step="any"
                maxValue={"100"}
              />
            )}
          />
        </div>

        <div className="col-12 font-normal font-weight-semibold pt-3 mb-2">
          Special Requirements
        </div>
        <div className="col-6 mb-2 pb-1">
          <Controller
            name="specialRequirements"
            control={control}
            render={({ onChange, value }) => (
              <textarea
                rows={4}
                className="form-control rounded-sm font-weight-semibold"
                value={value}
                onChange={onChange}
                placeholder={"Enter Special Requirements"}
              />
            )}
          />
        </div>

        <div className="col-12 font-normal font-weight-semibold pt-3 mb-2">
          <div className="row">
            <div className="col-6 mb-2 pb-1">
              <label className="font-sm font-weight-semibold mb-1">
                Core Requirement
              </label>
              <Controller
                name="coreRequirements"
                control={control}
                render={({ onChange, value }) => (
                  <CreatableSelect
                    components={{
                      DropdownIndicator,
                      IndicatorSeparator: () => null,
                    }}
                    isClearable
                    isMulti
                    value={value}
                    openMenuOnClick={false}
                    onChange={onChange}
                    placeholder="Enter core requirements"
                    className="custom-creatable-select"
                  />
                )}
              />
            </div>
            <div className="col-6 mb-2 pb-1">
              <label className="font-sm font-weight-semibold mb-1">
                Requirement Parameters
              </label>
              <Controller
                name="coreRequirementParameters"
                control={control}
                render={({ onChange, value }) => (
                  <CreatableSelect
                    components={{
                      DropdownIndicator,
                      IndicatorSeparator: () => null,
                    }}
                    isClearable
                    isMulti
                    value={value}
                    openMenuOnClick={false}
                    onChange={onChange}
                    placeholder="Enter requirement parameters"
                    className="custom-creatable-select"
                  />
                )}
              />
            </div>
          </div>
        </div>

        <div className="col-12 font-normal font-weight-semibold py-3 border-top mt-3">
          <button
            type="button"
            onClick={() => {
              reset();
              props.onClose?.();
            }}
            className="btn btn-outline-secondary font-base font-weight-bold mr-2 px-4 rounded"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary font-base font-weight-bold px-5 rounded"
            disabled={saving}
          >
            Save
          </button>
        </div>
      </div>
    </form>
  );
};
export default NewRequirementForm;
